import React, { useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { I18n, Translate } from 'react-redux-i18n';
import { useNavigate } from 'react-router-dom';

import Grid from '../../../components/common/grid';
import ContextMenu from '../../../components/common/grid/contextMenu';
import Panel from '../../../components/common/grid/managed/panel';
import { Common } from '../../../components';
import { gridKeys } from '../../../constants/gridKeys';
import { useAppDispatch } from '../../../hooks';
import RecordResultMessage from '../../../components/common/recordResult';
import { RootState } from '../../../store';
import { systemSettingsRoutes } from '../constants';

import {
  clearAccessKeyErrors,
  getAccessKeyListData,
  getAccessKeyMetadata,
  getAccessKeyListDataBySearchTerm,
  showDisableConfirmation
} from '../../accessKeys/store/accessKeyActions';
import {
  IAccessKeyElementModel,
  canDisableAccessKey
} from '../../../models/AccessKey';
import { DisableAccessKeyModal } from '../../accessKeys/modals/DisableAccessKeyModal';
import { WriteOnly } from '../../../components/common/grid/WriteOnly';
import { ReadOnlyContext } from '../../../components/common/grid/ReadOnlyContext';

const getPanel = (onClick: () => void) => (
  <Panel name="right">
    <WriteOnly>
      <Common.Button id="btnCreateAccessKey" primary onClick={onClick}>
        <Translate value="accessKeys.page.view.create-action" />
      </Common.Button>
    </WriteOnly>
  </Panel>
);

/**
 * Access Keys list content - designed to be embedded in SystemSettings container.
 */
const AccessKeysContent = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const accessKeyState = useSelector((s: RootState) => (s as any).accessKey);
  const session = useSelector((s: RootState) => s.session);

  const actions = useMemo(
    () =>
      bindActionCreators(
        {
          getMetadata: getAccessKeyMetadata,
          getListData: getAccessKeyListData,
          clearRecordMessages: clearAccessKeyErrors,
          getListDataBySearchTerm: getAccessKeyListDataBySearchTerm
        },
        dispatch
      ),
    [dispatch]
  );

  const onDisableClick = (_id: number, item: IAccessKeyElementModel) => {
    if (!item) return;
    dispatch(showDisableConfirmation(item) as any);
  };

  const isDisableMenuDisabled = (item: IAccessKeyElementModel) =>
    !canDisableAccessKey(item);

  const isReadOnly = useContext(ReadOnlyContext);

  const contextMenu = isReadOnly ? null : (
    <ContextMenu>
      <ContextMenu.Item
        key={'disable'}
        label={I18n.t('accessKeys.page.view.disable-action')}
        onClick={onDisableClick}
        isDisabled={isDisableMenuDisabled}
      />
    </ContextMenu>
  );

  const panel = getPanel(() => navigate(systemSettingsRoutes.ACCESS_KEYS_CREATE));

  const clearMessages = () =>
    dispatch(clearAccessKeyErrors(gridKeys.ACCESS_KEYS) as any);

  return (
    <>
      <RecordResultMessage
        record={accessKeyState.record}
        onDismiss={() => dispatch(clearAccessKeyErrors('record') as any)}
      />
      <h2>
        <Translate value="accessKeys.page.view.title" />
      </h2>
      <RecordResultMessage
        record={accessKeyState.accessKeysList}
        onDismiss={clearMessages}
      />
      <Grid.Managed
        listKey={gridKeys.ACCESS_KEYS}
        list={accessKeyState.accessKeysList}
        actions={{ ...actions }}
        search
        filtersButton={session.isSystemAdmin}
      >
        {panel}
        {contextMenu}
      </Grid.Managed>
      {accessKeyState.showDisableConfirmation && accessKeyState.selectedAccessKey && (
        <DisableAccessKeyModal accessKey={accessKeyState.selectedAccessKey} />
      )}
    </>
  );
};

export default AccessKeysContent;
